import { useState } from 'react';

import {
  Box,
  Collapse,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow
} from '@mui/material';

import SortableHeader, { SortOrder } from './SortableHeader';
import { ROW_HEIGHT } from './TableFilterRow';
import { TableListProps } from './types';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: SortOrder,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function TableList<
  T extends {
    [key in keyof T]: T[key];
  }
>({
  data,
  columns,
  page = 0,
  rowsPerPage = 25,
  defaultSortKey = 'id' as keyof T,
  defaultSortOrder = SortOrder.DESC,
  empty,
  action,
  onPaginationChange,
  children,
  expandFilters,
  loading,
  total,
  overflowXVisible = false
}: TableListProps<T>) {
  const [order, setOrder] = useState<SortOrder>(defaultSortOrder);
  const [orderBy, setOrderBy] = useState<keyof T>(defaultSortKey);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof T
  ) => {
    const isAsc = orderBy === property && order === SortOrder.ASC;
    setOrder(isAsc ? SortOrder.DESC : SortOrder.ASC);
    setOrderBy(property);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    onPaginationChange && onPaginationChange(page, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onPaginationChange &&
      onPaginationChange(0, parseInt(event.target.value, 10));
  };

  if (!data.length && !loading && empty) {
    return <>{empty}</>;
  }

  return (
    <>
      {/* children will be rendered as filtering section */}
      {children && (
        <Paper
          elevation={0}
          sx={{
            mb: 1,
            py: 2,
            px: 3
          }}
        >
          <Collapse in={expandFilters} collapsedSize={ROW_HEIGHT}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: 'column'
              }}
            >
              {children}
            </Box>
          </Collapse>
        </Paper>
      )}
      <Paper elevation={0}>
        <TableContainer
          style={{ overflowX: overflowXVisible ? 'visible' : 'auto' }}
        >
          {loading && (
            <LinearProgress
              aria-label="loading"
              sx={{
                backgroundColor: '#FDF2F7',
                '& .MuiLinearProgress-bar1Indeterminate, & .MuiLinearProgress-bar2Indeterminate':
                  {
                    backgroundColor: '#E21B70'
                  }
              }}
            />
          )}
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <SortableHeader<T>
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              hasAction={Boolean(action)}
            />
            <TableBody>
              {stableSort(data, getComparator(order, orderBy)).map(
                (row, index) => {
                  return (
                    <TableRow key={row[defaultSortKey]}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align="left">
                          {column.format ? column.format(row) : row[column.id]}
                        </TableCell>
                      ))}
                      {action && (
                        <TableCell
                          key={index}
                          align={'center'}
                          style={{ width: 50 }}
                        >
                          {action(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                }
              )}
            </TableBody>
            {onPaginationChange && (
              <TableFooter>
                {data && (
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[1, 25, 50, 100]}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      SelectProps={{
                        MenuProps: {
                          MenuListProps: {
                            title: 'rows per page'
                          }
                        }
                      }}
                    />
                  </TableRow>
                )}
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
